/**
 * OrganizationManagement - Hierarchy management for church/ministry structure
 * HQ → Province → Region → Country → District → Campus → Assembly → Home Group
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Building2, Users, ChevronRight, ChevronDown, Plus, Edit2, Edit, Trash2,
  MapPin, Phone, Mail, Globe, UserPlus, ArrowRightLeft, Search,
  FolderTree, UserCog, FileText, RefreshCw, Eye, DollarSign, Send
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { getToken, authHeaders } from '@/lib/authToken';
import LinkExistingDialog from './LinkExistingDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LEVEL_COLORS = {
  hq: 'bg-red-100 text-red-800 border-red-300',
  province: 'bg-orange-100 text-orange-800 border-orange-300',
  region: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  country: 'bg-green-100 text-green-800 border-green-300',
  district: 'bg-teal-100 text-teal-800 border-teal-300',
  campus: 'bg-blue-100 text-blue-800 border-blue-300',
  assembly: 'bg-indigo-100 text-indigo-800 border-indigo-300',
  home_group: 'bg-purple-100 text-purple-800 border-purple-300'
};

const LEVEL_ICONS = {
  hq: '🏛️',
  province: '🌍',
  region: '🗺️',
  country: '🏳️',
  district: '📍',
  campus: '🏢',
  assembly: '⛪',
  home_group: '🏠'
};

// Unit types for categorization (especially useful at campus level)
const UNIT_TYPES = {
  church: { label: 'Church', icon: '⛪' },
  school: { label: 'School', icon: '🏫' },
  hospital: { label: 'Hospital', icon: '🏥' },
  university: { label: 'University', icon: '🎓' },
  orphanage: { label: 'Orphanage', icon: '🏠' },
  community_center: { label: 'Community Center', icon: '🏘️' },
  mission_station: { label: 'Mission Station', icon: '⛺' },
  bible_college: { label: 'Bible College', icon: '📖' },
  rehabilitation_center: { label: 'Rehabilitation Center', icon: '💚' },
  media_center: { label: 'Media Center', icon: '📺' },
  office: { label: 'Administrative Office', icon: '🏢' },
  other: { label: 'Other', icon: '📍' }
};

const OrganizationManagement = () => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('hierarchy');
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [showCreateUnit, setShowCreateUnit] = useState(false);
  const [showCreateMember, setShowCreateMember] = useState(false);
  const [createParentId, setCreateParentId] = useState(null);
  const [createLevel, setCreateLevel] = useState(null);
  const [expandedUnits, setExpandedUnits] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [showEditUnit, setShowEditUnit] = useState(false);
  const [editUnitData, setEditUnitData] = useState(null);
  const [editMember, setEditMember] = useState(null);
  const [showLinkExisting, setShowLinkExisting] = useState(false);

  const token = getToken();
  // Lazy header builder — re-reads token from storage on every call so login/logout
  // in another tab can't leave us with stale `Bearer ` empty headers.
  const buildHeaders = () => ({ ...authHeaders(), 'Content-Type': 'application/json' });
  const headers = buildHeaders();

  // Fetch hierarchy levels
  const { data: levels = [] } = useQuery({
    queryKey: ['org-levels'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/admin/org/levels`, { headers });
      if (!res.ok) return [];
      return res.json();
    }
  });

  // Fetch leadership roles
  const { data: roles = [] } = useQuery({
    queryKey: ['org-roles'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/admin/org/roles`, { headers });
      if (!res.ok) return [];
      return res.json();
    }
  });

  // Fetch organization tree
  const { data: orgTree = [], isLoading: treeLoading, refetch: refetchTree } = useQuery({
    queryKey: ['org-tree'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/admin/org/tree`, { headers });
      if (!res.ok) return [];
      return res.json();
    }
  });

  // Fetch members
  const { data: membersData = { members: [], total: 0 }, isLoading: membersLoading } = useQuery({
    queryKey: ['org-members', selectedUnit?.id, searchTerm],
    queryFn: async () => {
      let url = `${API_URL}/api/admin/org/members?limit=100`;
      if (selectedUnit?.level === 'home_group') {
        url += `&home_group_id=${selectedUnit.id}`;
      } else if (selectedUnit?.level === 'assembly') {
        url += `&assembly_id=${selectedUnit.id}`;
      }
      if (searchTerm) {
        url += `&search=${encodeURIComponent(searchTerm)}`;
      }
      const res = await fetch(url, { headers });
      if (!res.ok) return { members: [], total: 0 };
      return res.json();
    },
    enabled: activeTab === 'members'
  });

  // Fetch pending registrations
  const { data: pendingRegistrations = [] } = useQuery({
    queryKey: ['org-registrations'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/admin/org/registrations?status=pending`, { headers });
      if (!res.ok) return [];
      return res.json();
    },
    enabled: activeTab === 'registrations'
  });

  // Create unit mutation
  const createUnitMutation = useMutation({
    mutationFn: async (data) => {
      const res = await fetch(`${API_URL}/api/admin/org/units`, {
        method: 'POST',
        headers: buildHeaders(),
        body: JSON.stringify(data)
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        if (res.status === 401) throw new Error('Session expired — please refresh and log in again.');
        throw new Error(err.detail || 'Failed to create unit');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['org-tree']);
      setShowCreateUnit(false);
      toast.success('Organization unit created');
    },
    onError: (error) => toast.error(error.message)
  });

  // Update / delete member mutations
  const updateMemberMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const res = await fetch(`${API_URL}/api/admin/org/members/${id}`, {
        method: 'PUT', headers: buildHeaders(), body: JSON.stringify(data),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to update member');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['org-members']);
      setEditMember(null);
      toast.success('Member updated');
    },
    onError: (e) => toast.error(e.message),
  });

  const deleteMemberMutation = useMutation({
    mutationFn: async (id) => {
      const res = await fetch(`${API_URL}/api/admin/org/members/${id}`, {
        method: 'DELETE', headers: buildHeaders(),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to remove member');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['org-members']);
      toast.success('Member removed');
    },
    onError: (e) => toast.error(e.message),
  });

  // Create member mutation
  const createMemberMutation = useMutation({
    mutationFn: async (data) => {
      const res = await fetch(`${API_URL}/api/admin/org/members`, {
        method: 'POST',
        headers: buildHeaders(),
        body: JSON.stringify(data)
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        if (res.status === 401) throw new Error('Session expired — please refresh and log in again.');
        throw new Error(err.detail || 'Failed to create member');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['org-members']);
      setShowCreateMember(false);
      toast.success('Member added');
    },
    onError: (error) => toast.error(error.message)
  });

  // Seed data mutation
  const seedMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${API_URL}/api/admin/org/seed`, {
        method: 'POST',
        headers
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed to seed data');
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(['org-tree']);
      if (data.seeded) {
        toast.success('Sample organization structure created!');
      } else {
        toast.info(data.message);
      }
    },
    onError: (error) => toast.error(error.message)
  });

  // Approve registration mutation
  const approveRegistrationMutation = useMutation({
    mutationFn: async ({ registrationId, homeGroupId }) => {
      const res = await fetch(`${API_URL}/api/admin/org/registrations/${registrationId}/approve?home_group_id=${homeGroupId}`, {
        method: 'POST',
        headers
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed to approve');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['org-registrations']);
      queryClient.invalidateQueries(['org-members']);
      toast.success('Registration approved');
    },
    onError: (error) => toast.error(error.message)
  });

  // Update unit mutation
  const updateUnitMutation = useMutation({
    mutationFn: async ({ unitId, data }) => {
      const res = await fetch(`${API_URL}/api/admin/org/units/${unitId}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(data)
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed to update unit');
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(['org-tree']);
      setShowEditUnit(false);
      setSelectedUnit(data);
      toast.success('Organization unit updated');
    },
    onError: (error) => toast.error(error.message)
  });

  // Delete unit mutation
  const deleteUnitMutation = useMutation({
    mutationFn: async (unitId) => {
      const res = await fetch(`${API_URL}/api/admin/org/units/${unitId}`, {
        method: 'DELETE',
        headers
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed to delete unit');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['org-tree']);
      setSelectedUnit(null);
      toast.success('Organization unit deleted');
    },
    onError: (error) => toast.error(error.message)
  });

  // Toggle expansion
  const toggleExpand = (unitId) => {
    const newExpanded = new Set(expandedUnits);
    if (newExpanded.has(unitId)) {
      newExpanded.delete(unitId);
    } else {
      newExpanded.add(unitId);
    }
    setExpandedUnits(newExpanded);
  };

  // Get next level for creating child
  const getNextLevel = (currentLevel) => {
    const levelOrder = ['hq', 'province', 'region', 'country', 'district', 'campus', 'assembly', 'home_group'];
    const idx = levelOrder.indexOf(currentLevel);
    return idx < levelOrder.length - 1 ? levelOrder[idx + 1] : null;
  };

  // Render tree node
  const renderTreeNode = (unit, depth = 0) => {
    const isExpanded = expandedUnits.has(unit.id);
    const hasChildren = unit.children && unit.children.length > 0;
    const nextLevel = getNextLevel(unit.level);
    
    // Get appropriate icon - use unit_type if available, otherwise level icon
    const unitTypeInfo = unit.unit_type && UNIT_TYPES[unit.unit_type];
    const displayIcon = unitTypeInfo ? unitTypeInfo.icon : LEVEL_ICONS[unit.level];

    return (
      <div key={unit.id} className="select-none">
        <div 
          className={`flex items-center gap-2 p-2 rounded-lg hover:bg-slate-100 cursor-pointer transition-colors ${
            selectedUnit?.id === unit.id ? 'bg-blue-50 border border-blue-200' : ''
          }`}
          style={{ marginLeft: depth * 20 }}
          onClick={() => setSelectedUnit(unit)}
          data-testid={`org-unit-${unit.id}`}
        >
          {hasChildren ? (
            <button 
              onClick={(e) => { e.stopPropagation(); toggleExpand(unit.id); }}
              className="p-1 hover:bg-slate-200 rounded"
            >
              {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </button>
          ) : (
            <span className="w-6" />
          )}
          
          <span className="text-lg">{displayIcon}</span>
          
          <div className="flex-1 min-w-0">
            <span className="font-medium truncate">{unit.name}</span>
            <Badge className={`ml-2 text-xs ${LEVEL_COLORS[unit.level]}`}>
              {unit.level.replace('_', ' ')}
            </Badge>
            {unitTypeInfo && (
              <Badge variant="outline" className="ml-1 text-xs">
                {unitTypeInfo.label}
              </Badge>
            )}
            {unit.external_ref && (
              <Badge
                variant="outline"
                className="ml-1 text-xs border-emerald-300 text-emerald-700 bg-emerald-50"
                title={`Linked from ${unit.external_ref.collection} · ${unit.external_ref.kind}`}
                data-testid={`org-unit-${unit.id}-linked`}
              >
                🔗 Linked {unit.external_ref.kind}
              </Badge>
            )}
          </div>
          
          <span className="text-sm text-slate-500">{unit.member_count || 0} members</span>
          
          {nextLevel && (
            <Button
              size="sm"
              variant="ghost"
              className="opacity-0 group-hover:opacity-100"
              onClick={(e) => {
                e.stopPropagation();
                setCreateParentId(unit.id);
                setCreateLevel(nextLevel);
                setShowCreateUnit(true);
              }}
            >
              <Plus className="h-4 w-4" />
            </Button>
          )}
        </div>
        
        {isExpanded && hasChildren && (
          <div>
            {unit.children.map(child => renderTreeNode(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  // Get all home groups for selection
  const getAllHomeGroups = (nodes, result = []) => {
    for (const node of nodes) {
      if (node.level === 'home_group') {
        result.push(node);
      }
      if (node.children) {
        getAllHomeGroups(node.children, result);
      }
    }
    return result;
  };

  const homeGroups = getAllHomeGroups(orgTree);

  return (
    <div className="space-y-6" data-testid="organization-management">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Building2 className="h-6 w-6 text-indigo-600" />
            Organization Hierarchy
          </h2>
          <p className="text-slate-600">Manage your ministry structure and members</p>
        </div>
        <div className="flex items-center gap-2">
          {orgTree.length === 0 && (
            <Button 
              variant="outline"
              onClick={() => seedMutation.mutate()}
              disabled={seedMutation.isPending}
            >
              {seedMutation.isPending ? 'Creating...' : '🌱 Create Sample Structure'}
            </Button>
          )}
          <Button variant="outline" onClick={() => refetchTree()}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button variant="outline" onClick={() => setShowLinkExisting(true)} data-testid="link-existing-btn">
            <span className="mr-2">🔗</span>
            Link existing
          </Button>
          <Button onClick={() => {
            setCreateParentId(null);
            setCreateLevel('hq');
            setShowCreateUnit(true);
          }}>
            <Plus className="h-4 w-4 mr-2" />
            Add Unit
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="hierarchy">
            <FolderTree className="h-4 w-4 mr-2" />
            Hierarchy
          </TabsTrigger>
          <TabsTrigger value="members">
            <Users className="h-4 w-4 mr-2" />
            Members
          </TabsTrigger>
          <TabsTrigger value="leadership">
            <UserCog className="h-4 w-4 mr-2" />
            Leadership
          </TabsTrigger>
          <TabsTrigger value="registrations">
            <FileText className="h-4 w-4 mr-2" />
            Registrations
            {pendingRegistrations.length > 0 && (
              <Badge className="ml-2 bg-red-500">{pendingRegistrations.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="finance">
            <DollarSign className="h-4 w-4 mr-2" />
            Finance
          </TabsTrigger>
          <TabsTrigger value="communications">
            <Send className="h-4 w-4 mr-2" />
            Communications
          </TabsTrigger>
        </TabsList>

        {/* Hierarchy Tab */}
        <TabsContent value="hierarchy" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Tree View */}
            <div className="lg:col-span-2">
              <Card>
                <CardHeader>
                  <CardTitle>Organization Structure</CardTitle>
                  <CardDescription>Click to select, use + to add children</CardDescription>
                </CardHeader>
                <CardContent>
                  {treeLoading ? (
                    <div className="text-center py-8 text-slate-500">Loading...</div>
                  ) : orgTree.length === 0 ? (
                    <div className="text-center py-12 bg-slate-50 rounded-lg">
                      <Building2 className="h-16 w-16 mx-auto mb-4 text-slate-300" />
                      <p className="text-slate-500 mb-4">No organization structure yet</p>
                      <Button onClick={() => seedMutation.mutate()} disabled={seedMutation.isPending}>
                        🌱 Create Sample Structure
                      </Button>
                      <p className="text-xs text-slate-400 mt-2">
                        This will create: HQ → Province → Region → Country → District → Campus → Assembly → Home Groups
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-1 max-h-[600px] overflow-auto">
                      {orgTree.map(unit => renderTreeNode(unit))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Unit Details */}
            <div>
              {selectedUnit ? (
                <Card>
                  <CardHeader>
                    <div className="flex items-center gap-2">
                      <span className="text-2xl">{LEVEL_ICONS[selectedUnit.level]}</span>
                      <div>
                        <CardTitle>{selectedUnit.name}</CardTitle>
                        <Badge className={LEVEL_COLORS[selectedUnit.level]}>
                          {selectedUnit.level.replace('_', ' ')}
                        </Badge>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {selectedUnit.description && (
                      <p className="text-sm text-slate-600">{selectedUnit.description}</p>
                    )}
                    
                    <div className="space-y-2 text-sm">
                      {selectedUnit.city && (
                        <div className="flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-slate-400" />
                          {selectedUnit.city}, {selectedUnit.country}
                        </div>
                      )}
                      {selectedUnit.phone && (
                        <div className="flex items-center gap-2">
                          <Phone className="h-4 w-4 text-slate-400" />
                          {selectedUnit.phone}
                        </div>
                      )}
                      {selectedUnit.email && (
                        <div className="flex items-center gap-2">
                          <Mail className="h-4 w-4 text-slate-400" />
                          {selectedUnit.email}
                        </div>
                      )}
                      {selectedUnit.website && (
                        <div className="flex items-center gap-2">
                          <Globe className="h-4 w-4 text-slate-400" />
                          <a href={selectedUnit.website} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">
                            {selectedUnit.website}
                          </a>
                        </div>
                      )}
                    </div>

                    <div className="pt-4 border-t">
                      <div className="grid grid-cols-2 gap-4 text-center">
                        <div>
                          <p className="text-2xl font-bold text-indigo-600">{selectedUnit.member_count || 0}</p>
                          <p className="text-xs text-slate-500">Total Members</p>
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-green-600">{selectedUnit.children?.length || 0}</p>
                          <p className="text-xs text-slate-500">Sub-units</p>
                        </div>
                      </div>
                    </div>

                    <div className="flex gap-2 pt-4">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="flex-1"
                        onClick={() => {
                          setEditUnitData({...selectedUnit});
                          setShowEditUnit(true);
                        }}
                      >
                        <Edit2 className="h-4 w-4 mr-1" />
                        Edit
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="flex-1 text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => {
                          if (window.confirm(`Are you sure you want to delete "${selectedUnit.name}"? This cannot be undone.`)) {
                            deleteUnitMutation.mutate(selectedUnit.id);
                          }
                        }}
                        disabled={deleteUnitMutation.isPending}
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        Delete
                      </Button>
                    </div>

                    {getNextLevel(selectedUnit.level) && (
                      <Button 
                        className="w-full"
                        onClick={() => {
                          setCreateParentId(selectedUnit.id);
                          setCreateLevel(getNextLevel(selectedUnit.level));
                          setShowCreateUnit(true);
                        }}
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Add {getNextLevel(selectedUnit.level).replace('_', ' ')}
                      </Button>
                    )}
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <CardContent className="py-12 text-center text-slate-500">
                    <Building2 className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>Select a unit to view details</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Members Tab */}
        <TabsContent value="members" className="mt-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Members Directory</CardTitle>
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input 
                      placeholder="Search members..." 
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-9 w-64"
                    />
                  </div>
                  <Button onClick={() => setShowCreateMember(true)}>
                    <UserPlus className="h-4 w-4 mr-2" />
                    Add Member
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {membersLoading ? (
                <div className="text-center py-8">Loading...</div>
              ) : membersData.members.length === 0 ? (
                <div className="text-center py-12 bg-slate-50 rounded-lg">
                  <Users className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                  <p className="text-slate-500">No members found</p>
                  {orgTree.length > 0 && (
                    <Button className="mt-4" onClick={() => setShowCreateMember(true)}>
                      <UserPlus className="h-4 w-4 mr-2" />
                      Add First Member
                    </Button>
                  )}
                </div>
              ) : (
                <div className="space-y-2">
                  {membersData.members.map((member) => (
                    <div 
                      key={member.id}
                      className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
                          <span className="text-indigo-600 font-medium">
                            {member.name?.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium">{member.name}</p>
                          <p className="text-sm text-slate-500">{member.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right text-sm">
                          <p className="text-slate-600">{member.home_group_name}</p>
                          <p className="text-slate-400">{member.phone}</p>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          title="Edit member"
                          onClick={() => setEditMember({ ...member })}
                          data-testid={`org-member-edit-${member.id}`}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          title="Transfer member"
                        >
                          <ArrowRightLeft className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                          title="Remove member"
                          onClick={() => {
                            if (window.confirm(`Remove ${member.name}? This will deactivate them; you can re-activate via Members.`)) {
                              deleteMemberMutation.mutate(member.id);
                            }
                          }}
                          data-testid={`org-member-delete-${member.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Leadership Tab */}
        <TabsContent value="leadership" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Leadership Roles</CardTitle>
              <CardDescription>Assign leaders to organization units</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {roles.map((role) => (
                  <div key={role.code} className="p-4 border rounded-lg">
                    <h4 className="font-medium">{role.title}</h4>
                    <p className="text-sm text-slate-500">{role.description}</p>
                    <Badge variant="outline" className="mt-2">{role.level}</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Registrations Tab */}
        <TabsContent value="registrations" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Pending Registrations</CardTitle>
              <CardDescription>Review and approve member registration requests</CardDescription>
            </CardHeader>
            <CardContent>
              {pendingRegistrations.length === 0 ? (
                <div className="text-center py-12 bg-slate-50 rounded-lg">
                  <FileText className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                  <p className="text-slate-500">No pending registrations</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {pendingRegistrations.map((reg) => (
                    <div key={reg.id} className="p-4 border rounded-lg">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">{reg.name}</p>
                          <p className="text-sm text-slate-500">{reg.email}</p>
                          <p className="text-xs text-slate-400">
                            Registered: {new Date(reg.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Select 
                            onValueChange={(homeGroupId) => {
                              approveRegistrationMutation.mutate({ 
                                registrationId: reg.id, 
                                homeGroupId 
                              });
                            }}
                          >
                            <SelectTrigger className="w-48">
                              <SelectValue placeholder="Select Home Group" />
                            </SelectTrigger>
                            <SelectContent>
                              {homeGroups.map((hg) => (
                                <SelectItem key={hg.id} value={hg.id}>
                                  {hg.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Finance Tab */}
        <TabsContent value="finance" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-emerald-600" />
                Organization Finance
              </CardTitle>
              <CardDescription>
                Manage tithes, offerings, expenses, and fund campaigns.
                {selectedUnit ? ` Viewing: ${selectedUnit.name}` : ' Select a unit from the hierarchy tab first.'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {!selectedUnit ? (
                <div className="text-center py-12 bg-slate-50 rounded-lg">
                  <DollarSign className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                  <p className="text-slate-500">Select an organization unit from the Hierarchy tab</p>
                  <p className="text-sm text-slate-400 mt-2">to view and manage its finances</p>
                  <Button 
                    variant="outline" 
                    className="mt-4"
                    onClick={() => setActiveTab('hierarchy')}
                  >
                    Go to Hierarchy
                  </Button>
                </div>
              ) : (
                <div className="space-y-6">
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                    <h4 className="font-medium text-emerald-800 mb-2">Finance Module Ready</h4>
                    <p className="text-sm text-emerald-700">
                      Finance management for <strong>{selectedUnit.name}</strong> is available.
                    </p>
                    <p className="text-sm text-emerald-600 mt-2">
                      Features include: Contributions recording, Expense tracking, Fund campaigns, Remittances between units, and Financial reports with hierarchy roll-up.
                    </p>
                    <div className="mt-4 flex flex-wrap gap-2">
                      <Badge variant="outline">Record Contributions</Badge>
                      <Badge variant="outline">Track Expenses</Badge>
                      <Badge variant="outline">Fund Campaigns</Badge>
                      <Badge variant="outline">Financial Reports</Badge>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <p className="text-sm text-slate-500">Income Categories</p>
                      <p className="text-lg font-bold">Tithe, Offering, Missions, Building Fund</p>
                    </div>
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <p className="text-sm text-slate-500">Expense Categories</p>
                      <p className="text-lg font-bold">Salaries, Utilities, Ministry, Welfare</p>
                    </div>
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <p className="text-sm text-slate-500">Reports</p>
                      <p className="text-lg font-bold">Summary, Roll-up, Statements</p>
                    </div>
                  </div>

                  <p className="text-sm text-slate-500">
                    API endpoints are ready for full finance functionality. 
                    Use <code className="bg-slate-100 px-1 rounded">/api/admin/org/units/{'{'}unit_id{'}'}/finance/*</code> endpoints.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Communications Tab */}
        <TabsContent value="communications" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Send className="h-5 w-5 text-blue-600" />
                Communications
              </CardTitle>
              <CardDescription>
                Send messages and announcements to members in your hierarchy.
                {selectedUnit ? ` Selected: ${selectedUnit.name}` : ' Select a unit first.'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {!selectedUnit ? (
                <div className="text-center py-12 bg-slate-50 rounded-lg">
                  <Send className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                  <p className="text-slate-500">Select an organization unit from the Hierarchy tab</p>
                  <p className="text-sm text-slate-400 mt-2">to send messages to members</p>
                  <Button 
                    variant="outline" 
                    className="mt-4"
                    onClick={() => setActiveTab('hierarchy')}
                  >
                    Go to Hierarchy
                  </Button>
                </div>
              ) : (
                <div className="space-y-6">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-medium text-blue-800 mb-2">Communications Ready</h4>
                    <p className="text-sm text-blue-700">
                      Send messages to all members under <strong>{selectedUnit.name}</strong>.
                    </p>
                    <p className="text-sm text-blue-600 mt-2">
                      Messages will be visible to members in the Member Portal and can optionally be sent via email (SMTP).
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="p-4 border rounded-lg">
                      <h4 className="font-medium mb-2">Create Message</h4>
                      <p className="text-sm text-slate-500">Compose announcements visible in member portals</p>
                      <Badge variant="outline" className="mt-2">In-App Messages</Badge>
                    </div>
                    <div className="p-4 border rounded-lg">
                      <h4 className="font-medium mb-2">Send Email</h4>
                      <p className="text-sm text-slate-500">Send messages directly to member emails</p>
                      <Badge variant="outline" className="mt-2">Email Notifications</Badge>
                    </div>
                  </div>

                  <p className="text-sm text-slate-500">
                    API endpoints ready: <code className="bg-slate-100 px-1 rounded">/api/admin/org/units/{'{'}unit_id{'}'}/messages</code> and 
                    <code className="bg-slate-100 px-1 rounded ml-1">/api/org/units/{'{'}unit_id{'}'}/send-message</code>
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Unit Dialog */}
      <Dialog open={showCreateUnit} onOpenChange={setShowCreateUnit}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Create {createLevel?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
            </DialogTitle>
          </DialogHeader>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const form = new FormData(e.target);
              createUnitMutation.mutate({
                name: form.get('name'),
                code: form.get('code'),
                level: createLevel,
                parent_id: createParentId,
                unit_type: form.get('unit_type') || null,
                description: form.get('description'),
                city: form.get('city'),
                country: form.get('country'),
                phone: form.get('phone'),
                email: form.get('email')
              });
            }}
            className="space-y-4"
          >
            <div>
              <label className="text-sm font-medium">Name *</label>
              <Input name="name" placeholder="e.g., Warsaw District" required />
            </div>
            
            <div>
              <label className="text-sm font-medium">Code *</label>
              <Input name="code" placeholder="e.g., district-warsaw" required />
              <p className="text-xs text-slate-500 mt-1">Unique identifier (lowercase, no spaces)</p>
            </div>
            
            {/* Unit Type - shown for campus level and below */}
            {['campus', 'assembly'].includes(createLevel) && (
              <div>
                <label className="text-sm font-medium">Unit Type</label>
                <Select name="unit_type" defaultValue="church">
                  <SelectTrigger>
                    <SelectValue placeholder="Select type..." />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(UNIT_TYPES).map(([key, { label, icon }]) => (
                      <SelectItem key={key} value={key}>
                        <span className="flex items-center gap-2">
                          <span>{icon}</span>
                          <span>{label}</span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500 mt-1">
                  Categorize as school, hospital, church, etc.
                </p>
              </div>
            )}
            
            <div>
              <label className="text-sm font-medium">Description</label>
              <Textarea name="description" placeholder="Brief description..." rows={2} />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">City</label>
                <Input name="city" placeholder="Warsaw" />
              </div>
              <div>
                <label className="text-sm font-medium">Country</label>
                <Input name="country" placeholder="Poland" />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Phone</label>
                <Input name="phone" placeholder="+48 123 456 789" />
              </div>
              <div>
                <label className="text-sm font-medium">Email</label>
                <Input name="email" type="email" placeholder="contact@..." />
              </div>
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreateUnit(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={createUnitMutation.isPending}>
                {createUnitMutation.isPending ? 'Creating...' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create Member Dialog */}
      <Dialog open={showCreateMember} onOpenChange={setShowCreateMember}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New Member</DialogTitle>
          </DialogHeader>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const form = new FormData(e.target);
              createMemberMutation.mutate({
                name: form.get('name'),
                email: form.get('email'),
                phone: form.get('phone'),
                home_group_id: form.get('home_group_id'),
                address: form.get('address'),
                gender: form.get('gender'),
                occupation: form.get('occupation')
              });
            }}
            className="space-y-4"
          >
            <div>
              <label className="text-sm font-medium">Full Name *</label>
              <Input name="name" placeholder="John Doe" required />
            </div>
            
            <div>
              <label className="text-sm font-medium">Email *</label>
              <Input name="email" type="email" placeholder="john@email.com" required />
            </div>
            
            <div>
              <label className="text-sm font-medium">Phone</label>
              <Input name="phone" placeholder="+1 234 567 890" />
            </div>
            
            <div>
              <label className="text-sm font-medium">Home Group *</label>
              <Select name="home_group_id" required>
                <SelectTrigger>
                  <SelectValue placeholder="Select home group" />
                </SelectTrigger>
                <SelectContent>
                  {homeGroups.map((hg) => (
                    <SelectItem key={hg.id} value={hg.id}>
                      {hg.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="text-sm font-medium">Gender</label>
              <Select name="gender">
                <SelectTrigger>
                  <SelectValue placeholder="Select gender" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="male">Male</SelectItem>
                  <SelectItem value="female">Female</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="text-sm font-medium">Address</label>
              <Textarea name="address" placeholder="Full address..." rows={2} />
            </div>
            
            <div>
              <label className="text-sm font-medium">Occupation</label>
              <Input name="occupation" placeholder="Software Developer" />
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreateMember(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={createMemberMutation.isPending}>
                {createMemberMutation.isPending ? 'Adding...' : 'Add Member'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Unit Dialog */}
      <Dialog open={showEditUnit} onOpenChange={setShowEditUnit}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Organization Unit</DialogTitle>
          </DialogHeader>
          {editUnitData && (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                updateUnitMutation.mutate({
                  unitId: editUnitData.id,
                  data: {
                    name: editUnitData.name,
                    code: editUnitData.code,
                    unit_type: editUnitData.unit_type,
                    description: editUnitData.description,
                    address: editUnitData.address,
                    city: editUnitData.city,
                    country: editUnitData.country,
                    phone: editUnitData.phone,
                    email: editUnitData.email,
                    website: editUnitData.website
                  }
                });
              }}
              className="space-y-4"
            >
              <div>
                <label className="text-sm font-medium">Name *</label>
                <Input 
                  value={editUnitData.name || ''} 
                  onChange={(e) => setEditUnitData({...editUnitData, name: e.target.value})}
                  placeholder="Unit name" 
                  required 
                />
              </div>
              
              <div>
                <label className="text-sm font-medium">Code</label>
                <Input 
                  value={editUnitData.code || ''} 
                  onChange={(e) => setEditUnitData({...editUnitData, code: e.target.value})}
                  placeholder="unique-code" 
                />
              </div>

              <div>
                <label className="text-sm font-medium">Type</label>
                <Select 
                  value={editUnitData.unit_type || 'church'} 
                  onValueChange={(v) => setEditUnitData({...editUnitData, unit_type: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(UNIT_TYPES).map(([key, { label, icon }]) => (
                      <SelectItem key={key} value={key}>{icon} {label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <label className="text-sm font-medium">Description</label>
                <Textarea 
                  value={editUnitData.description || ''} 
                  onChange={(e) => setEditUnitData({...editUnitData, description: e.target.value})}
                  placeholder="Brief description" 
                  rows={2}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium">City</label>
                  <Input 
                    value={editUnitData.city || ''} 
                    onChange={(e) => setEditUnitData({...editUnitData, city: e.target.value})}
                    placeholder="City" 
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Country</label>
                  <Input 
                    value={editUnitData.country || ''} 
                    onChange={(e) => setEditUnitData({...editUnitData, country: e.target.value})}
                    placeholder="Country" 
                  />
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium">Address</label>
                <Input 
                  value={editUnitData.address || ''} 
                  onChange={(e) => setEditUnitData({...editUnitData, address: e.target.value})}
                  placeholder="Full address" 
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium">Phone</label>
                  <Input 
                    value={editUnitData.phone || ''} 
                    onChange={(e) => setEditUnitData({...editUnitData, phone: e.target.value})}
                    placeholder="+1 234 567 890" 
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Email</label>
                  <Input 
                    value={editUnitData.email || ''} 
                    onChange={(e) => setEditUnitData({...editUnitData, email: e.target.value})}
                    placeholder="contact@unit.org" 
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium">Website</label>
                <Input 
                  value={editUnitData.website || ''} 
                  onChange={(e) => setEditUnitData({...editUnitData, website: e.target.value})}
                  placeholder="https://..." 
                />
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowEditUnit(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={updateUnitMutation.isPending}>
                  {updateUnitMutation.isPending ? 'Saving...' : 'Save Changes'}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Member Dialog */}
      <Dialog open={!!editMember} onOpenChange={(o) => !o && setEditMember(null)}>
        <DialogContent className="max-w-md" data-testid="org-edit-member-dialog">
          <DialogHeader>
            <DialogTitle>Edit member</DialogTitle>
          </DialogHeader>
          {editMember && (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const f = new FormData(e.target);
                updateMemberMutation.mutate({
                  id: editMember.id,
                  data: {
                    name: f.get('name'),
                    email: f.get('email'),
                    phone: f.get('phone'),
                    role: f.get('role') || null,
                  },
                });
              }}
              className="space-y-3"
            >
              <div>
                <label className="text-sm font-medium">Name *</label>
                <Input name="name" defaultValue={editMember.name} required />
              </div>
              <div>
                <label className="text-sm font-medium">Email</label>
                <Input name="email" type="email" defaultValue={editMember.email || ''} />
              </div>
              <div>
                <label className="text-sm font-medium">Phone</label>
                <Input name="phone" defaultValue={editMember.phone || ''} />
              </div>
              <div>
                <label className="text-sm font-medium">Role</label>
                <Input name="role" defaultValue={editMember.role || ''} placeholder="e.g. youth_leader" />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setEditMember(null)}>Cancel</Button>
                <Button type="submit" disabled={updateMemberMutation.isPending} data-testid="org-edit-member-save">
                  {updateMemberMutation.isPending ? 'Saving…' : 'Save'}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Link-existing dialog — pulls churches / schools / tenants into the tree */}
      <LinkExistingDialog
        open={showLinkExisting}
        onOpenChange={setShowLinkExisting}
        allUnits={orgTree}
        onLinked={() => { refetchTree(); }}
      />
    </div>
  );
};

export default OrganizationManagement;
